import { ClipboardModule } from '@angular/cdk/clipboard';
import { APP_BASE_HREF, CurrencyPipe, PlatformLocation } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  NO_ERRORS_SCHEMA,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { DalModule } from '@upr-web-primer/dal';
import { FormFieldsModule } from '@upr-web-primer/form-fields';
import { GdsFilterListModule } from '@upr-web-primer/gds-filter-list';
import { GdsFooterModule } from '@upr-web-primer/gds-footer';
import { GdsGlobalNavModule } from '@upr-web-primer/gds-global-nav';
import { GdsThemesService } from '@upr-web-primer/gds-themes';
import { GeolocationModule } from '@upr-web-primer/geolocation';
import { GoogleRecaptchaModule } from '@upr-web-primer/google-recaptcha';
import { LanguageSelectorModule } from '@upr-web-primer/language-selector';
import { K2LayoutModule } from '@upr-web-primer/layout-k2';
import { LocaleModule } from '@upr-web-primer/locale';
import { NotificationModule } from '@upr-web-primer/notification';
import { PageLabelsModule } from '@upr-web-primer/page-labels';
import { SimpleModalModule } from '@upr-web-primer/simple-modal';
import { TcmAnchorModule } from '@upr-web-primer/tcm-anchor';
import { TcmService } from '@upr-web-primer/tcm-service';
import {
  UdxSpinnerModule,
  UdxSpinnerService,
} from '@upr-web-primer/udx-spinner';
import { UprMobileServiceModule } from '@upr-web-primer/upr-mobile-service';
import { UrlInfoModule } from '@upr-web-primer/url-info';
import { ViewportModule } from '@upr-web-primer/viewport';
import { WindowModule } from '@upr-web-primer/window';
import { BnNgIdleService } from 'bn-ng-idle';
import { CookieService } from 'ngx-cookie-service';
import { HEADER_NAV_FOOTER_ENV_REPLACE } from '../config/header-nav-footer-config';
import { environment } from '../environments/environment';
import { AccountModule } from './account/account.module';
import { EntitlementModule } from './account/entitlements/entitlement.module';
import { AppLoadModule } from './app-load.module';
import { appProviders } from './app-providers';
import { AppComponent } from './app.component';
import { FooterComponent } from './layouts/footer/footer.component';
import { HeaderComponent } from './layouts/header/header.component';
import { RoutingModule } from './routing/routing.module';
import { AccountHttpInterceptor } from './services/account-http.interceptor';
import { AccountTcmService } from './services/account-tcm.service';
import { HttpErrorInterceptor } from './services/http-error.interceptor';
import { InitializationService } from './services/initialization.service';
import { PageResolver } from './shared/resolver/page.resolver';
import { provideGdsExternalSvgLoader } from '@upr-web-primer/gds-external-svg-loader';

export function appInitializerFactory(initService: InitializationService) {
  return (): Promise<any> => {
    return new Promise((resolve) => {
      initService.initializeApp().then(() => {
        resolve(true);
      });
    });
  };
}

@NgModule({
  exports: [NotificationModule],
  declarations: [AppComponent, HeaderComponent, FooterComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    EffectsModule.forRoot([]),
    BrowserAnimationsModule,
    WindowModule,
    ViewportModule,
    LocaleModule,
    DalModule,
    GeolocationModule,
    AppLoadModule,
    PageLabelsModule,
    K2LayoutModule,
    AccountModule,
    RoutingModule,
    TcmAnchorModule,
    UrlInfoModule,
    UdxSpinnerModule,
    NotificationModule,
    FormFieldsModule,
    GoogleRecaptchaModule,
    SimpleModalModule,
    UprMobileServiceModule.forRoot({} as any),
    StoreModule.forRoot({}),
    LanguageSelectorModule,
    GdsFilterListModule,
    GdsFooterModule,
    GdsGlobalNavModule,
    ClipboardModule,
    EntitlementModule,
  ],
  providers: [
    CurrencyPipe,
    appProviders,
    CookieService,
    BnNgIdleService,
    PageResolver,
    InitializationService,
    GdsThemesService,
    UdxSpinnerService,
    provideGdsExternalSvgLoader({
      baseUrl: environment.external_svg_base_url,
      strategy: 'on-demand',
    }),
    {
      provide: TcmService,
      useClass: AccountTcmService,
    },
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(
        inject(InitializationService),
      );
      return initializerFn();
    }),
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation): string => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: 'CustomEnv',
      useValue: { ...environment, ...HEADER_NAV_FOOTER_ENV_REPLACE },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccountHttpInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
